import { useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, imgUrl, formatApiError } from "@/lib/api";
import { ImageUploader } from "./ImageUploader";
import { inputCls } from "./ItemForm";

const CatDialog = ({ cat, onClose, onSaved }) => {
  const [f, setF] = useState({ name: cat?.name || "", description: cat?.description || "", cover_image: cat?.cover_image || "", order: cat?.order ?? 99 });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (cat?.id) await api.put(`/categories/${cat.id}`, f);
      else await api.post("/categories", f);
      toast.success("Category saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent className="bg-[#13141C] border-[#D4AF37]/25 text-[#F5F3EF] rounded-none" data-testid="category-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl font-normal">{cat?.id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="category-form-name" />
          <textarea rows={2} placeholder="Short description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={`${inputCls} h-auto py-2`} data-testid="category-form-description" />
          <input type="number" placeholder="Order" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} className={inputCls} data-testid="category-form-order" />
          <ImageUploader multiple={false} images={f.cover_image ? [f.cover_image] : []} onChange={(v) => setF({ ...f, cover_image: v[0] || "" })} testid="category-form-cover" />
          <button data-testid="category-form-save" className="w-full h-11 rounded-full bg-[#D4AF37] text-[#0D0D11] text-sm font-medium">Save category</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const CategoryManager = ({ cats, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category deleted");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <div>
      <button onClick={() => setEditing({})} data-testid="add-category-btn"
        className="mb-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#D4AF37] text-[#0D0D11] text-sm font-medium"><Plus className="w-4 h-4" /> New category</button>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cats.map((c) => (
          <div key={c.id} className="flex gap-4 border border-[#D4AF37]/20 bg-[#13141C] p-3" data-testid={`admin-cat-${c.slug}`}>
            <img src={imgUrl(c.cover_image)} alt="" className="w-20 h-20 object-cover bg-[#0D0D11]" />
            <div className="flex-1 min-w-0">
              <p className="font-display text-xl truncate">{c.name}</p>
              <p className="text-xs text-[#A09E97]">{c.count} items · order {c.order}</p>
              <div className="mt-2 flex gap-3">
                <button onClick={() => setEditing(c)} data-testid={`edit-cat-${c.slug}`} className="text-[#E5C158]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(c)} data-testid={`delete-cat-${c.slug}`} className="text-[#A09E97] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <CatDialog cat={editing} onClose={() => setEditing(null)} onSaved={onChanged} />}
    </div>
  );
};
